import { useEffect, useState } from "react";
import Header from "./components/Header";
import UserMenu from "./components/UserMenu";
import Button from "./components/Button";
import Card from "./components/Card";
import "./Home.css";

const FAQS = [
  { q: "Is WSNCode completely free?", a: "Yes! WSNCode is 100% free forever. No credit card, no subscription, no hidden charges." },
  { q: "Do I need to install anything?", a: "No installation needed. WSNCode runs entirely in your browser — just open the website and start coding instantly." },
  { q: "Can I save my work?", a: "Yes! Create a free account and save unlimited projects. Access them anytime from any device." },
  { q: "Which languages are supported?", a: "Currently HTML, CSS, and JavaScript are fully supported. More languages are on the way — join the waitlist to get notified first!" },
  { q: "Can I share my code with others?", a: "Absolutely! Every project has a shareable link. Share your work with friends, colleagues, or on social media." },
  { q: "Is WSNCode good for beginners?", a: "Yes! WSNCode is perfect for beginners. The live preview helps you see results instantly as you type." },
  { q: "Is WSNCode available in Pakistan and India?", a: "Yes! WSNCode is built for South Asian developers. Fast servers, no VPN needed, works great on local internet." },
  { q: "How is WSNCode different from CodePen?", a: "WSNCode is simpler, more affordable (Pro at $3 vs $8), and built specifically for students and beginner freelancers in South Asia. We focus on learning, not just building." },
];

const TESTIMONIALS = [
  { name: "Ahmed Raza",   role: "Android Developer",    location: "Lahore, Pakistan",  initials: "AR", color: "#00C2FF", text: "WSNCode saved me during my final year project. I was on my phone at 2am fixing CSS — the mobile editor actually worked. Couldn't believe it.", verified: "✓ Verified User" },
  { name: "Sara Khan",    role: "CS Student",            location: "Karachi",           initials: "SK", color: "#7C3AED", text: "I use this daily for my web dev assignments. The live preview is the best part — I can see my changes instantly without running anything locally.", verified: "✓ Verified User" },
  { name: "Usman Ali",   role: "Freelance Web Designer", location: "Islamabad",         initials: "UA", color: "#10B981", text: "I share project links with clients to show quick prototypes. Professional-looking demos in 10 minutes. My clients love it.", verified: "★ Freelancer on Fiverr" },
  { name: "Priya Mehta",  role: "Developer",             location: "Mumbai, India",     initials: "PM", color: "#f59e0b", text: "Finally a code playground that doesn't slow down on Indian internet. Fast, clean, and completely free. Love it!", verified: "✓ Verified User" },
  { name: "Bilal Hassan", role: "Freelance Dev",         location: "Dubai (Pakistani)", initials: "BH", color: "#00C2FF", text: "WSNCode is my go-to for quick HTML/CSS demos. Better than opening VSCode for a 5-minute fix. Fast, smooth, no fuss.", verified: "★ Top Rated Fiverr Seller" },
  { name: "Zara Malik",  role: "UI Designer",            location: "Rawalpindi",        initials: "ZM", color: "#ec4899", text: "The dark theme and clean UI make it actually enjoyable to code. I prefer this over other tools for quick stuff honestly.", verified: "✓ Verified User" },
];

// ── Typing animation hook ─────────────────────────────────────────────────────
function useTyping(text, speed = 35, delay = 0) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted]     = useState(false);

  useEffect(() => {
    const delayTimer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(delayTimer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    setDisplayed('');
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [started, text, speed]);

  return displayed;
}

// ── TypedHtml — renders HTML code with syntax colors ──
function TypedHtml({ text }) {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, li) => (
        <div key={li}>
          {line.split(/(<\/?[a-z0-9]+>)/gi).map((part, i) =>
            /^<\/?[a-z0-9]+>$/i.test(part)
              ? <span key={i} className="c-tag">{part}</span>
              : <span key={i} className="c-text">{part}</span>
          )}
        </div>
      ))}
    </>
  );
}

// ── Animated Code Preview ─────────────────────────────────────────────────────
function AnimatedCodePreview() {
  const htmlFull = `<h1>Hello WSNCode!</h1>\n<p>Live preview rocks!</p>`;
  const cssFull  = `h1 {\n  color: #00d4aa;\n  font-size: 2rem;\n}`;
  const jsFull   = `console.log("Hello!");`;

  const htmlText = useTyping(htmlFull, 40, 300);
  const cssText  = useTyping(cssFull,  40, htmlFull.length * 40 + 400);
  const jsText   = useTyping(jsFull,   40, (htmlFull.length + cssFull.length) * 40 + 500);

  const colorizeCss = (text) =>
    text.replace(/(h1)/g, `<span class="c-sel">$1</span>`)
        .replace(/(color|font-size)/g, `<span class="c-prop">$1</span>`)
        .replace(/(#00d4aa|2rem)/g, `<span class="c-val">$1</span>`);

  const colorizeJs = (text) =>
    text.replace(/(console)/g, `<span class="c-key">$1</span>`)
        .replace(/("Hello!")/g, `<span class="c-val">$1</span>`)
        .replace(/(\.log\(|\))/g, `<span class="c-text">$1</span>`);

  return (
    <div className="home-editor-preview">
      <div className="home-preview-bar">
        <span className="dot red"></span>
        <span className="dot yellow"></span>
        <span className="dot green"></span>
        <span className="home-preview-title">WSNCode — Live Editor</span>
      </div>
      <div className="home-preview-body">
        <div className="home-preview-panel">
          <div className="home-preview-label html">HTML</div>
          <div className="home-preview-code">
            <TypedHtml text={htmlText} />
            {htmlText.length < htmlFull.length && <span className="typing-cursor">|</span>}
          </div>
        </div>
        <div className="home-preview-panel">
          <div className="home-preview-label css">CSS</div>
          <div
            className="home-preview-code"
            dangerouslySetInnerHTML={{
              __html: colorizeCss(cssText) + (cssText.length < cssFull.length ? '<span class="typing-cursor">|</span>' : '')
            }}
          />
        </div>
        <div className="home-preview-panel">
          <div className="home-preview-label js">JS</div>
          <div
            className="home-preview-code"
            dangerouslySetInnerHTML={{
              __html: colorizeJs(jsText) + (jsText.length < jsFull.length ? '<span class="typing-cursor">|</span>' : '')
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Main Home Component ───────────────────────────────────────────────────────
export default function Home({ onGetStarted, onNewPen, user, onLogout, onSettings, onViewPens, onExplore, onBlog, onLogin }) {

  const [openFaq, setOpenFaq] = useState(null);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleNewPen = () => {
    if (onNewPen) onNewPen();
    else onGetStarted();
  };

  return (
    <div className="home">

      {/* ── NAVBAR ── */}
      <Header
        navItems={[
          { label: 'Editor',     onClick: onGetStarted },
          { label: 'Explore',    onClick: onExplore },
          { label: 'Challenges', onClick: () => {} },
          { label: 'Blog',       onClick: onBlog },
        ]}
        leftExtra={
          <div className="home-nav-anchors">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
        }
        authSlot={
          <div className="home-header-auth">
            {user ? (
              <UserMenu
                user={user}
                onLogout={onLogout}
                items={[
                  { label: 'New Pen',   icon: '✏️', onClick: handleNewPen },
                  { label: 'My Pens',   icon: '📁', onClick: onViewPens },
                  { label: 'Explore',   icon: '🔥', onClick: onExplore },
                  { label: 'Settings',  icon: '⚙️', onClick: onSettings },
                ]}
              />
            ) : (
              <>
                <Button variant="secondary" onClick={onLogin}>Login</Button>
                <Button variant="primary"   onClick={onGetStarted}>Start Coding Free →</Button>
              </>
            )}
          </div>
        }
        rightExtra={
          <button className="home-hamburger" onClick={() => {
            const menu = document.getElementById('mobile-menu');
            menu.style.display = menu.style.display === 'flex' ? 'none' : 'flex';
          }}>☰</button>
        }
      />

      {/* ── MOBILE MENU ── */}
      <div className="home-mobile-menu" id="mobile-menu">
        <Button variant="ghost" onClick={() => { onGetStarted(); document.getElementById('mobile-menu').style.display='none'; }}>✏️ Editor</Button>
        <Button variant="ghost" onClick={() => { onExplore();    document.getElementById('mobile-menu').style.display='none'; }}>🔥 Explore</Button>
        <Button variant="ghost" onClick={() => {                 document.getElementById('mobile-menu').style.display='none'; }}>🏆 Challenges</Button>
        <Button variant="ghost" onClick={() => { onBlog();       document.getElementById('mobile-menu').style.display='none'; }}>📝 Blog</Button>
        <div className="home-mobile-sep" />
        <a href="#features"     onClick={() => document.getElementById('mobile-menu').style.display='none'}>Features</a>
        <a href="#pricing"      onClick={() => document.getElementById('mobile-menu').style.display='none'}>Pricing</a>
        <a href="#languages"    onClick={() => document.getElementById('mobile-menu').style.display='none'}>Languages</a>
        <a href="#testimonials" onClick={() => document.getElementById('mobile-menu').style.display='none'}>Reviews</a>
        <a href="#faq"          onClick={() => document.getElementById('mobile-menu').style.display='none'}>FAQ</a>
        <div className="home-mobile-sep" />
        {user ? (
          <>
            <div className="mobile-user-info">
              <div className="hnd-avatar">{getInitials(user.name)}</div>
              <div>
                <p className="hnd-name">{user.name}</p>
                <p className="hnd-email">{user.email}</p>
              </div>
            </div>
            <Button variant="primary" onClick={() => { handleNewPen(); document.getElementById('mobile-menu').style.display='none'; }}>✏️ New Pen</Button>
            <Button variant="primary" onClick={() => { onViewPens();   document.getElementById('mobile-menu').style.display='none'; }}>📁 My Pens</Button>
            <Button variant="primary" onClick={() => { onSettings();   document.getElementById('mobile-menu').style.display='none'; }}>⚙️ Settings</Button>
            <Button variant="danger"  onClick={() => { onLogout();     document.getElementById('mobile-menu').style.display='none'; }}>🚪 Log Out</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onLogin}>Login</Button>
            <Button variant="primary"   onClick={onGetStarted}>Start Coding Free</Button>
          </>
        )}
      </div>

      {/* ── HERO ── */}
      <section className="home-hero">
        <div className="home-hero-badge">🚀 Built for Pakistani &amp; Indian Developers</div>
        <h1 className="home-hero-title">
          The Smartest Way to<br />
          <span>Learn &amp; Build</span> Front-End Code
        </h1>
        <p className="home-hero-desc">
          WSNCode is a free, powerful online code editor for HTML, CSS and JavaScript.
          No setup. No installation. Just open and start coding — instantly.
        </p>
        <div className="home-hero-btns">
          <Button variant="primary" size="lg" onClick={onGetStarted}>
            {user ? '✏️ Start Coding' : '⚡ Start Coding Free'}
          </Button>
          <a href="#features" className="home-cta-outline">See Features</a>
        </div>
        <AnimatedCodePreview />
      </section>

      {/* ── STATS ── */}
      <section className="home-stats">
        <div className="home-stat"><span className="home-stat-num">50+</span><span className="home-stat-label">Projects Created</span></div>
<div className="home-stat"><span className="home-stat-num">3</span><span className="home-stat-label">Languages Available</span></div>
<div className="home-stat"><span className="home-stat-num">0</span><span className="home-stat-label">Setup Required</span></div>
<div className="home-stat"><span className="home-stat-num">100%</span><span className="home-stat-label">Free Forever</span></div>
      </section>

     

      {/* ── FEATURES ── */}
      <section className="home-features" id="features">
        <div className="home-section-tag">⚡ Features</div>
        <h2 className="home-section-title">Everything You Need.<br />Nothing You Don't.</h2>
        <p className="home-section-desc">Powerful features designed for students, beginners, and freelancers.</p>
        <div className="home-features-grid">
          {[
            { icon: "⚡", title: "Live Preview",        desc: "See your code output instantly as you type — no need to refresh!" },
            { icon: "💾", title: "Save & Sync",         desc: "Save your work and come back to it anytime from any device." },
            { icon: "🎨", title: "Syntax Highlighting", desc: "Beautiful color-coded editor makes reading and writing code easier." },
            { icon: "📱", title: "Works on Any Device", desc: "Code on any device — desktop, tablet, or mobile phone." },
            { icon: "🔐", title: "Secure Login",        desc: "Login with Google, GitHub or email. Your projects are always safe." },
            { icon: "🆓", title: "Always Free",         desc: "No credit card. No subscription. Free forever. We mean it." },
          ].map((f, i) => (
            <Card hoverable className="home-feature-card" key={i}>
              <div className="home-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── LANGUAGES ── */}
      <section className="home-languages" id="languages">
        <div className="home-section-tag">🌐 Languages</div>
        <h2 className="home-section-title">Write Code in the Languages You Know</h2>
        <p className="home-section-desc">Start coding instantly. More languages coming soon.</p>
        <div className="home-lang-grid">
          <Card hoverable className="home-lang-card html">
            <span className="home-lang-icon">🌐</span>
            <span className="home-lang-name">HTML</span>
            <span className="home-lang-badge">✓ Available Now</span>
            <Button variant="primary" size="sm" onClick={onGetStarted}>Open Editor →</Button>
          </Card>
          <Card hoverable className="home-lang-card css">
            <span className="home-lang-icon">🎨</span>
            <span className="home-lang-name">CSS</span>
            <span className="home-lang-badge">✓ Available Now</span>
            <Button variant="primary" size="sm" onClick={onGetStarted}>Open Editor →</Button>
          </Card>
          <Card hoverable className="home-lang-card js">
            <span className="home-lang-icon">⚡</span>
            <span className="home-lang-name">JavaScript</span>
            <span className="home-lang-badge">✓ Available Now</span>
            <Button variant="primary" size="sm" onClick={onGetStarted}>Open Editor →</Button>
          </Card>
        </div>
        <div className="home-waitlist-row">
          <p>More languages on the way — <a href="#faq" className="home-waitlist-link">Join the waitlist →</a></p>
        </div>
      </section>

      {/* ── TECH STACK ── */}
      <section className="home-tech-stack">
        <div className="home-sp-inner">
          <div className="home-section-tag">🛠 Compatible</div>
          <h2 className="home-section-title">Works With Your Favorite Tools</h2>
          <p className="home-section-desc">Write vanilla code or use any framework — WSNCode handles it all</p>
          <div className="home-tech-chips">
            {["HTML5","CSS3","JavaScript","Bootstrap","Tailwind CSS","jQuery","Animate.css","Google Fonts"].map((t,i) => (
              <span className="home-tech-chip" key={i}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="home-testimonials" id="testimonials">
        <div className="home-section-tag">❤️ Reviews</div>
        <h2 className="home-section-title">What Developers Are Saying</h2>
        <p className="home-section-desc">Loved by 500+ students, designers and developers across South Asia</p>
        <div className="home-testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <Card hoverable className="home-testimonial-card" key={i}>
              <div className="home-testimonial-stars">★★★★★</div>
              <p className="home-testimonial-text">"{t.text}"</p>
              <div className="home-testimonial-author">
                <div className="home-testimonial-avatar" style={{background: t.color}}>{t.initials}</div>
                <div>
                  <p className="home-testimonial-name">{t.name}</p>
                  <p className="home-testimonial-role">{t.role} · {t.location}</p>
                  <span className="home-testimonial-verified">{t.verified}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="home-pricing" id="pricing">
        <div className="home-sp-inner">
          <div className="home-section-tag">💰 Pricing</div>
          <h2 className="home-section-title">Simple, Honest Pricing</h2>
          <p className="home-section-desc">Start free. Upgrade when you need more.</p>
          <div className="home-pricing-grid">

            {/* FREE */}
            <Card className="home-price-card">
              <div className="home-price-tier">Free</div>
              <div className="home-price-amount"><sup>$</sup>0<span className="home-price-per">/month</span></div>
              <div className="home-price-billing">Free forever. No credit card needed.</div>
              <hr className="home-price-divider" />
              <ul className="home-price-features">
                <li className="yes"><span className="chk">✓</span> Unlimited public projects</li>
                <li className="yes"><span className="chk">✓</span> HTML, CSS, JS support</li>
                <li className="yes"><span className="chk">✓</span> Live preview</li>
                <li className="yes"><span className="chk">✓</span> Save up to 10 projects</li>
                <li className="yes"><span className="chk">✓</span> Share project links</li>
                <li className="no"><span className="nochk">✗</span> Private projects</li>
                <li className="no"><span className="nochk">✗</span> Ad-free experience</li>
              </ul>
              <Button variant="secondary" onClick={onGetStarted}>Start Free →</Button>
            </Card>

            {/* PRO */}
            <Card className="home-price-card pro">
              <div className="home-popular-badge">⭐ Most Popular</div>
              <div className="home-price-tier">Pro</div>
              <div className="home-price-amount"><sup>$</sup>3<span className="home-price-per">/month</span></div>
              <div className="home-price-billing">Billed $29/year — save $7</div>
              <hr className="home-price-divider" />
              <ul className="home-price-features">
                <li className="yes"><span className="chk">✓</span> Everything in Free</li>
                <li className="yes"><span className="chk">✓</span> Unlimited projects</li>
                <li className="yes"><span className="chk">✓</span> <strong>Private projects</strong></li>
                <li className="yes"><span className="chk">✓</span> <strong>No ads</strong></li>
                <li className="yes"><span className="chk">✓</span> <strong>Custom project URL</strong></li>
                <li className="yes"><span className="chk">✓</span> Priority support</li>
              </ul>
              <Button variant="primary" onClick={onGetStarted}>Get Pro →</Button>
            </Card>

            {/* STUDENT */}
            <Card className="home-price-card">
              <div className="home-price-tier">Student</div>
              <div className="home-price-amount"><sup>$</sup>1<span className="home-price-per">/month</span></div>
              <div className="home-price-billing">Verify with .edu email</div>
              <hr className="home-price-divider" />
              <ul className="home-price-features">
                <li className="yes"><span className="chk">✓</span> Everything in Pro</li>
                <li className="yes"><span className="chk">✓</span> <strong>Verified Student badge</strong></li>
                <li className="yes"><span className="chk">✓</span> <strong>Download as HTML file</strong></li>
                <li className="yes"><span className="chk">✓</span> Learning resources</li>
              </ul>
              <Button variant="secondary" onClick={onGetStarted}>Verify Student Status →</Button>
            </Card>

          </div>

          {/* Pricing mini FAQ */}
          <div className="home-pricing-faq">
            <div className="home-pfaq-item">
              <h4>Can I cancel anytime?</h4>
              <p>Yes, cancel anytime. No questions asked, no hidden fees.</p>
            </div>
            <div className="home-pfaq-item">
              <h4>Is the free plan really free?</h4>
              <p>Yes, forever. No credit card needed ever.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CHALLENGES ── */}
      <section className="home-challenges">
        <div className="home-sp-inner">
          <div className="home-section-tag">🏆 Community</div>
          <h2 className="home-section-title">Weekly Coding Challenges</h2>
          <p className="home-section-desc">Build your skills. Win recognition. Get featured.</p>
          <div className="home-challenges-grid">
            <Card hoverable className="home-challenge-card">
              <div className="home-challenge-label">🔥 This Week's Challenge</div>
              <div className="home-challenge-title">Build a CSS Animated Button</div>
              <div className="home-challenge-sub">Create a pure CSS button with a smooth hover animation. Best design wins a featured spot.</div>
              <Button variant="secondary" onClick={onGetStarted}>Join Challenge →</Button>
            </Card>
            <Card hoverable className="home-challenge-card">
              <div className="home-challenge-label">🥇 Last Week's Winner</div>
              <div className="home-challenge-title">Ahmed Raza Won!</div>
              <div className="home-challenge-sub">His glassmorphism card design got 47 ❤️ from the community. Pure CSS magic.</div>
              <Button variant="secondary" onClick={onGetStarted}>See Project →</Button>
            </Card>
            <Card hoverable className="home-challenge-card">
              <div className="home-challenge-label">⭐ All Time Best</div>
              <div className="home-challenge-title">Pure CSS Animation</div>
              <div className="home-challenge-sub">The most-liked challenge of all time. 200+ submissions, 1,400+ views.</div>
              <Button variant="secondary" onClick={onExplore}>View All →</Button>
            </Card>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="home-faq" id="faq">
        <div className="home-section-tag">❓ FAQ</div>
        <h2 className="home-section-title">Frequently Asked Questions</h2>
        <p className="home-section-desc">Got questions? We have answers.</p>
        <div className="home-faq-list">
          {FAQS.map((faq, i) => (
            <Card
              className={`home-faq-item ${openFaq === i ? 'open' : ''}`}
              key={i}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <div className="home-faq-question">
                <span>{faq.q}</span>
                <span className="home-faq-icon">{openFaq === i ? '−' : '+'}</span>
              </div>
              {openFaq === i && (
                <div className="home-faq-answer">{faq.a}</div>
              )}
            </Card>
          ))}
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="home-cta-section" id="about">
        <h2>{user ? `Welcome back, ${user.name}!` : 'Ready to Write Your First Project?'}</h2>
        <p>{user ? 'Continue where you left off.' : 'Join 500+ developers already coding on WSNCode. No setup. No credit card. No excuses.'}</p>
        <div className="home-bottom-cta-btns">
          <Button variant="primary" size="lg" onClick={onGetStarted}>
            {user ? '✏️ Start Coding Now' : '⚡ Start Coding Free →'}
          </Button>
          {!user && <a href="#pricing" className="home-cta-outline">See Pricing →</a>}
        </div>
        <div className="home-cta-stars">
          <span className="home-cta-star-icons">★★★★★</span> &nbsp;Rated by developers across Pakistan, India &amp; beyond
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="home-footer">
        <div className="home-footer-inner">
          <div className="home-footer-brand">
            <img src="/logo.svg" alt="WSNCode" className="home-footer-logo" />
            <p className="home-footer-desc">A free code editor built for<br />South Asian developers.</p>
          </div>
          <div className="home-footer-col">
            <h4>Features</h4>
            <a href="#features">Editor</a>
            <a href="#languages">Languages</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="home-footer-col">
            <h4>Community</h4>
            <a href="#challenges">Challenges</a>
            <button className="home-footer-link-btn" onClick={onExplore}>Trending Projects</button>
            <a href="#challenges">Weekly Winner</a>
          </div>
          <div className="home-footer-col">
            <h4>Company</h4>
            <button className="home-footer-link-btn" onClick={onBlog}>Blog</button>
            <a href="#faq">Contact</a>
            <a href="#faq">Privacy Policy</a>
          </div>
        </div>
        <div className="home-footer-bottom">
          <p className="home-footer-copy">© 2026 WSNCode. Built with ❤️ in Pakistan.</p>
          <div className="home-footer-socials">
            <span className="home-footer-social-link">Twitter/X</span>
            <span className="home-footer-social-link">YouTube</span>
            <span className="home-footer-social-link">Instagram</span>
            <span className="home-footer-social-link">LinkedIn</span>
          </div>
        </div>
      </footer>

    </div>
  );
}